import React, { useState, useEffect, useContext } from 'react';
import { api } from '../../utils/requestInterceptor';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import format from 'date-fns/format';
import parse from 'date-fns/parse';
import startOfWeek from 'date-fns/startOfWeek';
import getDay from 'date-fns/getDay';
import { useNavigate } from 'react-router-dom';
import ViewsNavigation from './ViewsNavigation';
import { useRaciAssignments } from '../../hooks/useRaciAssignments';
import { AuthContext } from '../../contexts/AuthContext';
import { createCalendarDate, getDueDateStatusColor } from '../../utils/dateUtils';

const locales = {
  'en-US': require('date-fns/locale/en-US')
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales
});


// Get initials from a user's name
const getInitials = (name) => {
  if (!name) return '';
  
  const names = name.split(' ');
  if (names.length === 1) return names[0].charAt(0).toUpperCase();
  
  return (names[0].charAt(0) + names[names.length - 1].charAt(0)).toUpperCase();
};

// User colors for consistent color coding
const userColors = {};
const getColorForUser = (userId) => {
  if (userColors[userId]) return userColors[userId];
  
  // Generate a consistent color based on the userId
  const colorOptions = [
    '#3B82F6', // blue-500
    '#8B5CF6', // violet-500
    '#EC4899', // pink-500
    '#14B8A6', // teal-500
    '#F59E0B', // amber-500
    '#6366F1', // indigo-500
    '#10B981', // emerald-500
    '#06B6D4', // cyan-500
    '#A855F7', // purple-500
    '#F43F5E', // rose-500
  ];
  
  // Deterministic selection based on userId
  const index = parseInt(userId) % colorOptions.length;
  userColors[userId] = colorOptions[index];
  
  return userColors[userId];
};

const DueDateCalendar = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [view, setView] = useState('month');
  const [departments, setDepartments] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedDepartmentId, setSelectedDepartmentId] = useState('all');
  const [selectedUserId, setSelectedUserId] = useState('all');
  const navigate = useNavigate();
  const { getBatchAssignments } = useRaciAssignments();
  const { currentUser } = useContext(AuthContext);

  // Set the default user filter to current user when component mounts
  useEffect(() => {
    // Only set if currentUser exists and selectedUserId hasn't been explicitly changed
    if (currentUser && currentUser.id && selectedUserId === 'all') {
      setSelectedUserId(currentUser.id.toString());
    }
  }, [currentUser, selectedUserId]);

  // Fetch departments
  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const response = await api.get('/departments');
        if (response.data && response.data.departments) {
          setDepartments(response.data.departments);
        }
      } catch (error) {
        console.error('Error fetching departments:', error);
      }
    };
    
    fetchDepartments();
  }, []);

  // Fetch users
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await api.get('/auth/users');
        if (response.data && response.data.users) {
          setUsers(response.data.users);
          
          // Set default user filter to current user if logged in
          if (currentUser && currentUser.id) {
            setSelectedUserId(currentUser.id.toString());
          }
        }
      } catch (error) {
        console.error('Error fetching users:', error);
      }
    };
    
    fetchUsers();
  }, [currentUser]);

  // Fetch projects data
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch projects with due dates
        const response = await api.get('/projects', {
          params: {
            with_due_dates: true
          }
        });
        
        if (response.data && response.data.projects) {
          // Get projects with due dates and filter out completed/canceled projects
          const projectsWithDueDates = response.data.projects
            .filter(p => p.due_date)
            .filter(project => {
              const status = project.status ? project.status.toLowerCase() : '';
              return !status.includes('complete') && 
                     !status.includes('finished') && 
                     !status.includes('done') && 
                     !status.includes('cancel') && 
                     !status.includes('abandon');
            });
          
          // Use the batch assignments hook to get all assignments at once
          const projectIds = projectsWithDueDates.map(project => project.id);
          const batchAssignments = await getBatchAssignments('project', projectIds);
          
          // Map the assignments to projects
          const projectsWithAssignments = projectsWithDueDates.map(project => {
            const projectAssignments = batchAssignments[project.id] || { project: { assignments: {} } };
            const accountableUsers = projectAssignments.project?.assignments?.A || [];
            
            return {
              ...project,
              accountableUsers
            };
          });
          
          // Create calendar events
          const calendarEvents = projectsWithAssignments
            .filter(project => {
              // Filter by department if selected
              if (selectedDepartmentId !== 'all' && project.department_id !== parseInt(selectedDepartmentId)) {
                return false;
              }
              
              // Filter by accountable user if selected
              if (selectedUserId === 'unassigned') {
                // Show only projects with no accountable users
                return project.accountableUsers.length === 0;
              } else if (selectedUserId !== 'all') {
                return project.accountableUsers.some(
                  assignment => assignment.user_id === parseInt(selectedUserId)
                );
              }
              
              return true;
            })
            .map(project => {
              // Get the accountable users for this project
              const accountableUserIds = project.accountableUsers.map(a => a.user_id);
              const accountableUserNames = accountableUserIds.map(userId => {
                const user = users.find(u => u.id === userId);
                return user ? user.name : 'Unknown';
              });
              
              // Get initials for display
              const initialsDisplay = accountableUserIds.length === 0 
                ? '[UNASSIGNED] ' 
                : `[${accountableUserNames.map(name => getInitials(name)).join(', ')}] `;
              
              // Create an event for the project
              const eventDate = createCalendarDate(project.due_date);
              return {
                id: `project-${project.id}`,
                title: `${initialsDisplay}${project.name}`,
                start: eventDate,
                end: eventDate,
                allDay: true,
                resource: {
                  project,
                  accountableUserIds,
                  isUnassigned: accountableUserIds.length === 0,
                  dueDateColor: getDueDateStatusColor(project.due_date)
                }
              };
            });
          
          setEvents(calendarEvents);
        }
      } catch (error) {
        console.error('Error fetching projects:', error);
        setError('Failed to load projects for calendar view');
      } finally {
        setLoading(false);
      }
    };
    
    fetchProjects();
  }, [selectedDepartmentId, selectedUserId, users, getBatchAssignments]);

  // Style event based on due date status and user
  const eventStyleGetter = (event) => {
    const resource = event.resource || {};
    const dueDateColor = resource.dueDateColor || '#6B7280'; // gray-500 default
    
    // Get the color for the first accountable user (if any)
    const userColor = resource.isUnassigned
      ? '#6B7280' // gray-500 for unassigned projects
      : resource.accountableUserIds && resource.accountableUserIds.length > 0
      ? getColorForUser(resource.accountableUserIds[0])
      : '#3B82F6'; // blue-500 default
    
    // Custom style for unassigned items (dashed border)
    const borderStyle = resource.isUnassigned ? 'dashed' : 'solid';
    
    return {
      style: {
        backgroundColor: userColor,
        borderLeft: `4px ${borderStyle} ${dueDateColor}`,
        color: 'white',
        borderRadius: '4px',
        display: 'block',
        padding: '2px 5px'
      }
    };
  };

  // Handle click on event to navigate to project
  const handleEventClick = (event) => {
    const resource = event.resource;
    if (resource && resource.project) {
      navigate(`/projects/${resource.project.id}`);
    }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#111827', borderRadius: '8px', padding: '20px' }}>
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold text-white">Project Due Dates</h1>
          <p className="mt-2 text-sm text-gray-300">
            A calendar view of all project due dates. Click on an event to view details.
          </p>
        </div>
      </div>

      <ViewsNavigation />

      <div className="mt-6 bg-gray-800 p-4 shadow-sm rounded-lg border border-gray-700">
        {/* Filters */}
        <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label htmlFor="department-filter" className="block text-sm font-medium text-gray-300 mb-1">
              Filter by Department
            </label>
            <select
              id="department-filter"
              value={selectedDepartmentId}
              onChange={(e) => setSelectedDepartmentId(e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-600 bg-gray-700 text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            >
              <option value="all">All Departments</option>
              {departments.map((department) => (
                <option key={department.id} value={department.id}>
                  {department.name}
                </option>
              ))}
            </select>
          </div>
          
          <div>
            <label htmlFor="user-filter" className="block text-sm font-medium text-gray-300 mb-1">
              Filter by Accountable User
            </label>
            <select
              id="user-filter"
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-600 bg-gray-700 text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            >
              <option value="all">All Users</option>
              <option value="unassigned">Unassigned</option>
              {users.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.name}
                </option>
              ))}
            </select>
          </div>
          
          <div>
            <label htmlFor="view-selector" className="block text-sm font-medium text-gray-300 mb-1">
              Calendar View
            </label>
            <select
              id="view-selector"
              value={view}
              onChange={(e) => setView(e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-600 bg-gray-700 text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            >
              <option value="month">Month</option>
              <option value="week">Week</option>
              <option value="day">Day</option>
            </select>
          </div>
        </div>
        
        {/* Legend */}
        <div className="mb-6 bg-gray-700 p-4 rounded-md shadow-sm border border-gray-600">
          <h3 className="text-sm font-medium text-white mb-2">Due Date Status:</h3>
          <div className="flex space-x-4">
            <div className="flex items-center">
              <div className="w-4 h-4 bg-green-500 rounded mr-2"></div>
              <span className="text-sm text-gray-300">Due in more than 7 days</span>
            </div>
            <div className="flex items-center">
              <div className="w-4 h-4 bg-orange-500 rounded mr-2"></div>
              <span className="text-sm text-gray-300">Due within 7 days</span>
            </div>
            <div className="flex items-center">
              <div className="w-4 h-4 bg-red-500 rounded mr-2"></div>
              <span className="text-sm text-gray-300">Overdue</span>
            </div>
          </div>
        </div>
        
        {/* Calendar */}
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
          </div>
        ) : error ? (
          <div className="bg-red-50 p-4 rounded-md">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-red-800">{error}</p>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-white p-4 rounded-md shadow-sm border border-gray-200 h-[700px]">
            <Calendar
              localizer={localizer}
              events={events}
              startAccessor="start"
              endAccessor="end"
              style={{ height: '100%' }}
              views={['month', 'week', 'day']}
              view={view}
              onView={setView}
              eventPropGetter={eventStyleGetter}
              onSelectEvent={handleEventClick}
              popup
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default DueDateCalendar; 